import { useState } from 'react';
import {
  BarChart3,
  Heart,
  Home,
  LineChart,
  Search,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useLocation } from '@remix-run/react';
import TickerSearchModal from './TickerSearchModal';
import { useFavoritesCount } from '~/ui-apps/hooks/useFavoritesCount';
import {
  isFinancialGlanceTickerPath,
  isLegacyCompanyTickerPath,
} from '~/utilities/tickerRoutePaths.util';
import { getUserFromContext } from '~/utilities/userContext';

interface MobileFooterStickyProps {
  isAuthenticated: boolean;
}

type TabClassArgs = {
  active: boolean;
};

function tabClassName({ active }: TabClassArgs) {
  return `relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-800/40 focus-visible:ring-offset-1 ${active ? 'text-emerald-900' : 'text-gray-500'
    }`;
}

function TabLabel({
  label,
  active,
}: {
  label: string;
  active: boolean;
}) {
  return (
    <>
      <span
        className={`max-w-full truncate text-[10px] leading-tight tracking-tight ${active ? 'font-semibold' : 'font-medium'
          }`}
      >
        {label}
      </span>
      <span
        className={`mt-0.5 h-1 w-1 rounded-full ${active ? 'bg-emerald-900' : 'bg-transparent'
          }`}
        aria-hidden
      />
    </>
  );
}

function TabIcon({
  icon: Icon,
  active,
}: {
  icon: LucideIcon;
  active: boolean;
}) {
  return (
    <Icon
      className="h-[22px] w-[22px] shrink-0"
      strokeWidth={active ? 2.25 : 1.75}
      aria-hidden
    />
  );
}

const MobileFooterSticky = (_props: MobileFooterStickyProps) => {
  const location = useLocation();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [tickerSearchModalMode, setTickerSearchModalMode] = useState<
    'financials' | 'insider-activity' | null
  >(null);
  const { user } = getUserFromContext();
  const userId = user?.id ? Number(user.id) : undefined;
  const { count: favoritesCount, displayCount: favoritesDisplayCount } =
    useFavoritesCount(userId, true);

  const openSearchModal = () => {
    setTickerSearchModalMode(null);
    setShowSearchModal(true);
  };

  const homeActive =
    location.pathname === '/home' || location.pathname.startsWith('/home/');
  const screenerActive =
    location.pathname === '/screener' || location.pathname.startsWith('/screener/');
  const searchActive =
    isLegacyCompanyTickerPath(location.pathname) ||
    isFinancialGlanceTickerPath(location.pathname);
  const priceModelsActive =
    location.pathname === '/price-models' || location.pathname.startsWith('/price-models/');
  const favoritesActive =
    location.pathname === '/favorites' ||
    location.pathname.startsWith('/favorites/');

  return (
    <>
      <nav
        id="mobile-footer-nav-bar"
        aria-label="Primary"
        className="lg:hidden fixed inset-x-0 bottom-0 z-20 border-t border-gray-200/80 bg-background pb-[env(safe-area-inset-bottom,0px)]"
      >
        <div className="flex items-stretch justify-around px-1 pt-2 pb-1.5">
          <Link
            to="/home"
            prefetch="intent"
            aria-current={homeActive ? 'page' : undefined}
            className={tabClassName({ active: homeActive })}
          >
            <TabIcon icon={Home} active={homeActive} />
            <TabLabel label="Home" active={homeActive} />
          </Link>

          <Link
            to="/screener"
            prefetch="intent"
            aria-current={screenerActive ? 'page' : undefined}
            className={tabClassName({ active: screenerActive })}
          >
            <TabIcon icon={BarChart3} active={screenerActive} />
            <TabLabel label="Screener" active={screenerActive} />
          </Link>

          <button
            type="button"
            onClick={openSearchModal}
            aria-label="Search ticker"
            className={tabClassName({ active: searchActive })}
          >
            <TabIcon icon={Search} active={searchActive} />
            <TabLabel label="Search" active={searchActive} />
          </button>

          <Link
            to="/price-models"
            prefetch="intent"
            aria-current={priceModelsActive ? 'page' : undefined}
            className={tabClassName({ active: priceModelsActive })}
          >
            <TabIcon icon={LineChart} active={priceModelsActive} />
            <TabLabel label="Forecasts" active={priceModelsActive} />
          </Link>

          <Link
            to="/favorites"
            prefetch="intent"
            aria-current={favoritesActive ? 'page' : undefined}
            aria-label={
              favoritesCount > 0
                ? `Favorites (${favoritesCount})`
                : 'Favorites'
            }
            className={tabClassName({ active: favoritesActive })}
          >
            <span className="relative inline-flex">
              <Heart
                className="h-[22px] w-[22px] shrink-0"
                strokeWidth={favoritesActive ? 2.25 : 1.75}
                fill={favoritesActive ? 'currentColor' : 'none'}
                aria-hidden
              />
              {favoritesCount > 0 ? (
                <span
                  className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-bold leading-none tabular-nums text-white"
                  aria-hidden="true"
                >
                  {favoritesDisplayCount}
                </span>
              ) : null}
            </span>
            <TabLabel label="Favorites" active={favoritesActive} />
          </Link>
        </div>
      </nav>

      {showSearchModal && (
        <TickerSearchModal
          showSearchModal={showSearchModal}
          setShowSearchModal={setShowSearchModal}
          mode={tickerSearchModalMode}
        />
      )}
    </>
  );
};

export default MobileFooterSticky;
